import { extractPropertyName } from '@salesrun/property-name';
import { OverridenFunctionHandle } from './OverridenFunctionHandle';

/**
 * Function which allows overriding another object's own function.
 *
 * @export
 * @template T - Type of the object which holds the function
 * @template TF - Type of the function to override (must be a method of T)
 * @param {T} context - Object which holds the function
 * @param {(ctx: T) => TF} pathFn - Property-getting function for the method to override
 * @param {(originalFn: TF, ...args: Parameters<TF>) => ReturnType<TF>} extendedBehaviour - Function that receives the original function and its arguments
 * @returns {OverridenFunctionHandle} Handle to revert the override
 */
export function overrideFunction<
    T extends object,
    TF extends ((...args: unknown[]) => unknown) & T[keyof T]
>(
    context: T,
    pathFn: (ctx: T) => TF,
    extendedBehaviour: (originalFn: TF, ...args: Parameters<TF>) => ReturnType<TF>,
): OverridenFunctionHandle {
    const prop = extractPropertyName<T>(pathFn);
    const originalFnRef = context[prop] as TF;
    const overridenFnRef = (...args: Parameters<TF>) => {
        return extendedBehaviour(originalFnRef.bind(context) as TF, ...args);
    };

    context[prop] = overridenFnRef as TF;

    return {
        revert: () => {
            context[prop] = originalFnRef;
        },
    };
}